import { create } from 'zustand';

/**
 * Dark-mode theme store (N1 — spec: docs/night-shift-mode-spec.md §6).
 *
 * localStorage-backed: the inline FOUC guard in index.html applies the saved
 * `.dark` class before first paint; this store keeps document + UI in sync
 * afterwards. The `User.themePreference` DB column and GA rollout land in
 * phase N4 — until then the toggle renders for super admins only (dev
 * preview while the N2 component sweep completes).
 */
export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'avium:theme';

function readSaved(): ThemeMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function applyToDocument(theme: ThemeMode) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

interface ThemeStore {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeStore>((set, get) => ({
  theme: readSaved(),
  setTheme: (theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* private mode — theme holds for this session only */
    }
    applyToDocument(theme);
    set({ theme });
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
}));

// Re-assert on module load: keeps the class correct after hot reloads where
// the FOUC guard doesn't re-run, and makes jsdom tests deterministic.
applyToDocument(readSaved());
